/**
 * The visitor's past conversations, as this app reasons about them — P2-52.
 *
 * All pure, and all three functions exist because the alternative is a decision
 * taken inline in JSX where nobody can test it: which word labels a status, what
 * a row says when there is nothing to preview, and what happens to a list when
 * the same conversation arrives on two pages.
 */
import type { WidgetMessageId } from '../i18n/messages';

import type { WidgetConversationSummary } from '../api/contract';

/**
 * The word beside the date on a history row.
 *
 * THREE LABELS FOR FIVE CATEGORIES, and the collapse is deliberate rather than
 * lazy. `closed` and `resolved` are the two the visitor can act on differently:
 * `closed` is TERMINAL for them (ADR-0142 — their next message opens a NEW
 * conversation), while `resolved` is not (a reply still lands in the same
 * thread). `open`, `pending` and `snoozed` are three words for one visitor-facing
 * fact — somebody still has this — and the difference between them is a queue
 * state that belongs to the business, not to the person waiting.
 *
 * `statusCategory` is an OPEN string on the wire, deliberately: a widget in
 * someone else's page updates when their CDN cache expires, so a category added
 * next quarter reaches this build. It falls back to "Open" rather than to
 * nothing — a row with no status reads as broken, and "open" is the honest
 * summary of every category that is not one of the two terminal-ish ones.
 */
export function statusLabelId(statusCategory: string): WidgetMessageId {
  if (statusCategory === 'closed') {
    return 'widget.status.closed';
  }
  if (statusCategory === 'resolved') {
    return 'widget.status.resolved';
  }
  return 'widget.status.open';
}

/**
 * The second line of a history row.
 *
 * `preview` is null before the first message, and the API derives it from
 * `message` under the visitor policy — so what the policy hides cannot reach
 * this function. A body that is entirely whitespace is treated as no preview:
 * rendering it produces a row with a blank second line, which looks like a
 * loading failure rather than like a conversation nobody wrote in.
 */
export type PreviewLine =
  { readonly kind: 'preview'; readonly text: string } | { readonly kind: 'none' };

export function previewLine(preview: string | null): PreviewLine {
  if (preview === null) {
    return { kind: 'none' };
  }
  const trimmed = preview.trim();
  return trimmed === '' ? { kind: 'none' } : { kind: 'preview', text: trimmed };
}

/**
 * Add a page of history to the rows already on screen.
 *
 * DEDUPLICATED BY ID, and that is not defensive coding: the paging is keyset on
 * `lastActivityAt` and the visitor's own live conversation moves to the top of
 * that ordering every time anybody says anything. So while page two is in
 * flight, a row from page one can legitimately arrive again — and without the
 * dedupe the visitor sees the same conversation twice, with two different
 * previews, which reads as two conversations.
 *
 * SORTED HERE TOO, for the same reason: once two pages are merged, "the server
 * sent them in order" is a statement about each page and not about the list.
 * `Date.parse` rather than a string compare, because these are `z.iso.datetime()`
 * values and nothing in that type promises one offset — an ISO string with an
 * offset sorts lexicographically by the digits somebody wrote it in rather than
 * by the instant it names. An unparseable value sorts last rather than throwing:
 * a row with a broken timestamp is still a conversation the visitor had.
 */
export function mergeHistory(
  existing: readonly WidgetConversationSummary[],
  incoming: readonly WidgetConversationSummary[],
): readonly WidgetConversationSummary[] {
  const byId = new Map<string, WidgetConversationSummary>();
  for (const summary of existing) {
    byId.set(summary.id, summary);
  }
  // Incoming wins on a collision: it is the newer read of the same row, and the
  // thing most likely to have changed is the preview the visitor is looking at.
  for (const summary of incoming) {
    byId.set(summary.id, summary);
  }

  return [...byId.values()].sort((left, right) => {
    const a = activityOrder(left);
    const b = activityOrder(right);
    // Not `b - a`: two unparseable rows are both -Infinity, and the subtraction
    // of two infinities is NaN — a comparator that returns NaN leaves the sort
    // order unspecified rather than merely wrong.
    if (a === b) {
      return 0;
    }
    return a < b ? 1 : -1;
  });
}

function activityOrder(summary: WidgetConversationSummary): number {
  const parsed = Date.parse(summary.lastActivityAt);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
